import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { announceAvererProvider, openAuthPopup, type AvererProviderHandle } from "@averer/wallet-sdk/direct";
import { AuthContext, useAvererAccountId } from "../../auth/AuthProvider";
import { useToast } from "../../designSystem";
import { EmbeddedWalletSessionContext } from "../dynamic/embeddedWalletSession";
import { getRuntimeViteEnv } from "../../runtimeEnv";

const CONNECT_FAILED_TITLE = "Couldn't connect your wallet. Please try again.";
export const SIGN_IN_WITH_AVERER_TITLE = "To connect your wallet, sign out and sign in with Averer.";

const AvererProviderContext = createContext<AvererProviderHandle | null>(null);

export const useAvererProviderHandle = (): AvererProviderHandle | null => useContext(AvererProviderContext);

const toAddresses = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.length > 0) : [];

export const AvererWalletProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const walletRuntimeUrl = getRuntimeViteEnv("VITE_WALLET_RUNTIME_URL");
  const { currentUser } = useContext(AuthContext);
  const avererAccountId = useAvererAccountId();
  const { enqueue } = useToast();
  const [provider, setProvider] = useState<AvererProviderHandle | null>(null);
  const [addresses, setAddresses] = useState<string[]>([]);
  // Set only while a click-driven connect runs; the automatic attempt must never open a popup.
  const clickConnectRef = useRef(false);

  useEffect(() => {
    // Without an Averer account there is nothing to bind the wallet to, so nothing is announced.
    if (!walletRuntimeUrl || !avererAccountId) {
      return undefined;
    }
    const { provider: handle, unmount } = announceAvererProvider({
      walletRuntimeUrl,
      authBootstrap: {
        enabled: true,
        // Averer refuses the wallet when its signed-in account is not the one this session signed in as.
        expectedAccount: { kind: "sub", sub: avererAccountId },
        // Browsers block a popup that no click opened, so only a click may open the sign-in popup.
        openAuthPopup: (options) =>
          clickConnectRef.current ? openAuthPopup(options) : Promise.reject(new Error("Averer sign-in needs a click")),
      },
    });
    const onAccountsChanged = (accounts: unknown): void => setAddresses(toAddresses(accounts));
    handle.on("accountsChanged", onAccountsChanged);
    setProvider(handle);
    return () => {
      handle.removeListener("accountsChanged", onAccountsChanged);
      unmount();
      setProvider(null);
      setAddresses([]);
    };
  }, [walletRuntimeUrl, avererAccountId]);

  const signedIn = Boolean(currentUser);
  useEffect(() => {
    // A connected address must not outlive the Tokeniser session it was connected under.
    if (!signedIn) {
      setAddresses([]);
      return undefined;
    }
    if (!provider) {
      return undefined;
    }
    let active = true;
    provider
      .request({ method: "eth_requestAccounts" })
      .then((accounts) => {
        if (active) {
          setAddresses(toAddresses(accounts));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [provider, signedIn]);

  const session = useMemo(
    () => ({
      addresses,
      showAuthFlow: () => {
        if (!provider) {
          // A password sign-in has no Averer account, so retrying can never connect.
          const needsAvererSignIn = signedIn && Boolean(walletRuntimeUrl) && !avererAccountId;
          enqueue({ tone: "error", title: needsAvererSignIn ? SIGN_IN_WITH_AVERER_TITLE : CONNECT_FAILED_TITLE });
          return;
        }
        clickConnectRef.current = true;
        provider
          .request({ method: "eth_requestAccounts" })
          .then((accounts) => setAddresses(toAddresses(accounts)))
          .catch(() => enqueue({ tone: "error", title: CONNECT_FAILED_TITLE }))
          .finally(() => {
            clickConnectRef.current = false;
          });
      },
      // ponytail: the SDK exposes no logout; forgetting the address is enough to stop signing from this app.
      disconnect: async () => setAddresses([]),
    }),
    [addresses, avererAccountId, enqueue, provider, signedIn, walletRuntimeUrl]
  );

  return (
    <AvererProviderContext.Provider value={provider}>
      <EmbeddedWalletSessionContext.Provider value={session}>{children}</EmbeddedWalletSessionContext.Provider>
    </AvererProviderContext.Provider>
  );
};
